import { ReactNode } from 'react';
import ColorizedPanel from '@/creditflowresearch/ui/common/components/molecules/ColorizedPanel';
import { type IFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import { type IAccessCondition } from '@/creditflowresearch/ui/common/hooks/access/useAccessConditions';
import { useProductPageFilters } from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader/hooks/useProductPageFilters';
import { useProductPageAccess } from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader/hooks/useProductPageAccess';
import { useProductPageTitle } from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader/hooks/useProductPageTitle';
import ProductPageHeaderContent from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader/ProductPageHeaderContent';

import productColors from '@/creditflowresearch/ui/common/styles/productColors';

interface IProductPageHeaderProps {
  title: string | ReactNode,
  items?: ReactNode | IFilterSet[],
  accessConditions?: IAccessCondition[],
  blockingAccessConditions?: IAccessCondition[],
  isDisabled?: boolean,
  onToggle?: (searchQuery: string, filterName: string, queryName: string) => void,
  withoutProductTitle?: boolean,
  panelBackground: string,
}

const ProductPageHeader = ({
  title,
  items,
  accessConditions,
  blockingAccessConditions,
  isDisabled = false,
  onToggle,
  withoutProductTitle = false,
  panelBackground,
}: IProductPageHeaderProps) => {
  const { getActiveFilters, getActiveItems, isFilterMode, renderItems } = useProductPageFilters({
    items,
    isDisabled,
    onToggle,
  });

  const productAbbreviations = withoutProductTitle || !isFilterMode ? [] : getActiveFilters('products');
  const activeProductItems = isFilterMode ? getActiveItems('products') : [];

  const { shouldShowLock, hasBlockingAccessFailure } = useProductPageAccess({
    activeProductItems,
    accessConditions,
    blockingAccessConditions,
  });

  const formattedTitle = useProductPageTitle({
    title,
    productAbbreviations,
    shouldShowLock,
  });

  return (
    <ColorizedPanel style={ { background: panelBackground || productColors.gradientProductAbs, marginBottom: '20px' } }>
      <ProductPageHeaderContent
        title={ formattedTitle }
        items={ renderItems }
        hasBlockingAccessFailure={ hasBlockingAccessFailure }
      />
    </ColorizedPanel>
  );
};

export default ProductPageHeader;
